import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api.ts';

// Typed hooks for the management screens. Each mutation refreshes what it changes.

export interface Member {
  id: string;
  userId: string;
  displayName: string;
  email: string | null;
  status: 'active' | 'suspended' | 'ended';
  role: { id: string; name: string };
  team: { id: string; name: string } | null;
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  roleId: string;
  teamId: string | null;
  expiresAt: string;
  createdAt: string;
  emailSent?: boolean;
}

export interface Role {
  id: string;
  name: string;
  baseKey: string | null;
  permissions: string[];
  memberCount: number;
}

export interface Team {
  id: string;
  name: string;
  memberCount: number;
}

export interface MySession {
  id: string;
  current: boolean;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  impersonation: boolean;
}

/** A browser that skips the 2FA code ("Recordar este dispositivo"). */
export interface TrustedDevice {
  id: string;
  /** This browser. */
  current: boolean;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
}

export interface SecuritySettings {
  mfaRequired: boolean;
  /** "Recordar este dispositivo": 14 or 30 days; null: off. */
  trustedDeviceDays: 14 | 30 | null;
}

export const keys = {
  members: (former: boolean) => ['members', { former }] as const,
  invitations: ['invitations'] as const,
  roles: ['roles'] as const,
  assignableRoles: ['roles', 'assignable'] as const,
  teams: ['teams'] as const,
  mySessions: ['my-sessions'] as const,
  trustedDevices: ['trusted-devices'] as const,
  security: ['settings', 'security'] as const,
};

export const useMembers = (includeFormer: boolean) =>
  useQuery({
    queryKey: keys.members(includeFormer),
    queryFn: () => api<Member[]>(`/members${includeFormer ? '?includeFormer=true' : ''}`),
  });
export const useInvitations = () =>
  useQuery({ queryKey: keys.invitations, queryFn: () => api<Invitation[]>('/invitations') });
export const useRoles = () =>
  useQuery({ queryKey: keys.roles, queryFn: () => api<Role[]>('/roles') });
export const useAssignableRoles = () =>
  useQuery({
    queryKey: keys.assignableRoles,
    queryFn: () => api<{ id: string; name: string }[]>('/roles/assignable'),
  });
export const useTeams = () =>
  useQuery({ queryKey: keys.teams, queryFn: () => api<Team[]>('/teams') });
export const useMySessions = () =>
  useQuery({ queryKey: keys.mySessions, queryFn: () => api<MySession[]>('/auth/sessions') });
/** Not during an impersonation (the API refuses it): pass enabled = false then. */
export const useTrustedDevices = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: keys.trustedDevices,
    queryFn: () => api<TrustedDevice[]>('/auth/trusted-devices'),
  });
export const useSecuritySettings = () =>
  useQuery({
    queryKey: keys.security,
    queryFn: () => api<SecuritySettings>('/settings/security'),
  });

/** A mutation that refreshes the given query keys when it succeeds. */
export function useApiMutation<TInput, TResult = unknown>(
  run: (input: TInput) => Promise<TResult>,
  invalidate: readonly (readonly unknown[])[],
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await Promise.all(invalidate.map((queryKey) => client.invalidateQueries({ queryKey })));
    },
  });
}

/** Re-enter a TOTP code before a sensitive action (one action per step-up). */
export const reauthenticate = (code: string) =>
  api('/auth/reauthenticate', { method: 'POST', body: { code } });
